import {
  Component,
  Inject,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from 'rxjs';

import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environment/environment';
import { OrganizationCustomerDTO } from '../../../dto/organization/organization-customer.dto';
import { bindDanishPostalCodeCityLookup } from '../../../utils/dk-postal-code.util';
import {
  applyCustomerCvrLookup,
  fetchCustomerByCvr,
  normalizeDanishCvr,
} from '../../../utils/customer-cvr-lookup.util';

export interface DunaCustomersCreateDialogData {
  initialName?: string;
}

interface PriceGroupOption {
  id: number;
  label: string;
}

@Component({
  selector: 'app-duna-customers-create-dialog',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
  ],
  template: `
    <h2 mat-dialog-title>Tilføj ny kunde</h2>
    <mat-dialog-content>
      <form [formGroup]="form" class="create-form">
        <div class="field-row">
          <mat-form-field appearance="outline" class="cvr-field">
            <mat-label>CVR</mat-label>
            <input
              matInput
              formControlName="cvr"
              placeholder="12345678"
              autocomplete="off"
            />
            <button
              mat-icon-button
              matSuffix
              type="button"
              aria-label="Hent oplysninger fra CVR"
              matTooltip="Hent fra CVR"
              [disabled]="!canFetchCvr || cvrLookupLoading"
              (click)="fetchFromCvr()"
            >
              @if (cvrLookupLoading) {
              <mat-progress-spinner
                diameter="20"
                mode="indeterminate"
              ></mat-progress-spinner>
              } @else {
              <mat-icon>download</mat-icon>
              }
            </button>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>EAN</mat-label>
            <input
              matInput
              formControlName="ean"
              placeholder="13-cifret EAN"
              autocomplete="off"
            />
          </mat-form-field>
        </div>
        @if (cvrLookupHint) {
        <p class="cvr-hint" [class.cvr-hint--error]="cvrLookupError">
          {{ cvrLookupHint }}
        </p>
        }

        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>Navn *</mat-label>
          <input
            matInput
            formControlName="name"
            placeholder="Firmanavn"
            autocomplete="off"
          />
          @if (form.get('name')?.hasError('required')) {
          <mat-error>Navn er påkrævet</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>Att.</mat-label>
          <input
            matInput
            formControlName="att"
            placeholder="Kontaktperson"
            autocomplete="off"
          />
        </mat-form-field>

        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>E-mail</mat-label>
          <input
            matInput
            formControlName="email"
            type="email"
            placeholder="kontakt@firma.dk"
            autocomplete="off"
          />
        </mat-form-field>

        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>Telefonnummer</mat-label>
          <input
            matInput
            formControlName="phone"
            placeholder="+45 12 34 56 78"
            autocomplete="off"
          />
        </mat-form-field>

        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>Mobilnummer</mat-label>
          <input
            matInput
            formControlName="mobile"
            placeholder="+45 12 34 56 78"
            autocomplete="off"
          />
        </mat-form-field>

        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>Adresse</mat-label>
          <input
            matInput
            formControlName="address"
            placeholder="Gade og husnummer"
            autocomplete="off"
          />
        </mat-form-field>

        <div class="field-row">
          <mat-form-field appearance="outline">
            <mat-label>Postnummer</mat-label>
            <input
              matInput
              formControlName="postal_code"
              placeholder="1234"
              autocomplete="off"
            />
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>By</mat-label>
            <input
              matInput
              formControlName="city"
              placeholder="København"
              autocomplete="off"
            />
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>Prisgruppe</mat-label>
          <mat-select
            formControlName="price_group_id"
            [compareWith]="comparePriceGroupIds"
          >
            <mat-option [value]="0">Ingen</mat-option>
            @for (g of priceGroups; track g) {
            <mat-option [value]="g.id">{{ g.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        @if (error) {
        <p class="error-msg"><mat-icon>error</mat-icon> {{ error }}</p>
        }
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button
        mat-stroked-button
        (click)="dialogRef.close(false)"
        [disabled]="isSubmitting"
      >
        Annuller
      </button>
      <button
        mat-raised-button
        color="primary"
        [disabled]="form.invalid || isSubmitting"
        (click)="onSave()"
      >
        @if (isSubmitting) {
        <mat-progress-spinner
          diameter="18"
          mode="indeterminate"
          style="display:inline-block;margin-right:6px"
        ></mat-progress-spinner>
        } @if (!isSubmitting) {
        <mat-icon>add</mat-icon>
        } Tilføj
      </button>
    </mat-dialog-actions>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .create-form {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding-top: 8px;
        min-width: 420px;
      }
      .field-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
      }
      .error-msg {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #f44336;
        font-size: 0.875rem;
        margin: 0;
        mat-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
        }
      }
      .cvr-hint {
        margin: -2px 0 6px;
        font-size: 0.85rem;
        color: #2e7d32;
      }
      .cvr-hint--error {
        color: #c62828;
      }
    `,
  ],
})
export class DunaCustomersCreateDialogComponent implements OnDestroy {
  form: FormGroup;
  isSubmitting = false;
  error: string | null = null;
  priceGroups: PriceGroupOption[] = [];
  cvrLookupLoading = false;
  cvrLookupHint: string | null = null;
  cvrLookupError = false;
  private postalCodeLookupSub?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<
      DunaCustomersCreateDialogComponent,
      OrganizationCustomerDTO | false
    >,
    private fb: FormBuilder,
    private http: HttpClient,
    @Inject(MAT_DIALOG_DATA) data: DunaCustomersCreateDialogData | null
  ) {
    const initialName = data?.initialName?.trim() ?? '';
    this.form = this.fb.group({
      name: [initialName, Validators.required],
      att: [''],
      email: [''],
      phone: [''],
      mobile: [''],
      address: [''],
      postal_code: [''],
      city: [''],
      ean: [''],
      cvr: [''],
      price_group_id: [0],
    });
    this.postalCodeLookupSub = bindDanishPostalCodeCityLookup(
      this.form,
      this.http
    );
    this.http
      .get<{ items?: PriceGroupOption[] }>(
        `${environment.apiUrl}/events/price-groups`,
        {
          params: { activeOnly: 'true' },
        }
      )
      .subscribe({
        next: (res) => {
          this.priceGroups = (res?.items ?? []).filter((g) => g.id > 0);
        },
        error: () => {
          this.priceGroups = [];
        },
      });
  }

  ngOnDestroy(): void {
    this.postalCodeLookupSub?.unsubscribe();
  }

  get canFetchCvr(): boolean {
    return normalizeDanishCvr(this.form.get('cvr')?.value) !== null;
  }

  fetchFromCvr(): void {
    const cvr = normalizeDanishCvr(this.form.get('cvr')?.value);
    if (!cvr || this.cvrLookupLoading) {
      return;
    }

    this.cvrLookupLoading = true;
    this.cvrLookupHint = null;
    this.cvrLookupError = false;

    fetchCustomerByCvr(this.http, environment.apiUrl, cvr).subscribe({
      next: (data) => {
        this.cvrLookupLoading = false;
        const applied = applyCustomerCvrLookup(this.form, data);
        if (applied) {
          this.cvrLookupHint = 'Oplysninger hentet fra CVR.';
        } else {
          this.cvrLookupError = true;
          this.cvrLookupHint =
            'Virksomheden blev fundet, men der var ingen adresseoplysninger.';
        }
      },
      error: (err) => {
        this.cvrLookupLoading = false;
        this.cvrLookupError = true;
        this.cvrLookupHint =
          err?.error?.message || 'Kunne ikke hente oplysninger fra CVR.';
      },
    });
  }

  comparePriceGroupIds = (
    a: number | null | undefined,
    b: number | null | undefined
  ): boolean => {
    const left = this.normalizePriceGroupId(a);
    const right = this.normalizePriceGroupId(b);
    return left === right;
  };

  private normalizePriceGroupId(value: unknown): number | null {
    if (value === null || value === undefined || value === '' || value === 0) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
  }

  onSave(): void {
    if (this.form.invalid || this.isSubmitting) return;
    this.isSubmitting = true;
    this.error = null;

    const payload = {
      name: this.form.value.name.trim(),
      att: this.form.value.att?.trim() || null,
      email: this.form.value.email?.trim() || null,
      phone: this.form.value.phone?.trim() || null,
      mobile: this.form.value.mobile?.trim() || null,
      address: this.form.value.address?.trim() || null,
      postal_code: this.form.value.postal_code?.trim() || null,
      city: this.form.value.city?.trim() || null,
      ean: this.form.value.ean?.trim() || null,
      cvr: this.form.value.cvr?.trim() || null,
      price_group_id: this.normalizePriceGroupId(
        this.form.value.price_group_id
      ),
    };

    this.http
      .post<OrganizationCustomerDTO>(
        `${environment.apiUrl}/organizations/customers`,
        payload
      )
      .subscribe({
        next: (customer) => this.dialogRef.close(customer),
        error: (err) => {
          this.isSubmitting = false;
          this.error = err?.error?.message || 'Der opstod en fejl. Prøv igen.';
        },
      });
  }
}
